import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box, TextField, InputAdornment, Switch, FormControlLabel,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { dmsApi } from '../services/dmsApi';
import useDmsPalette from '../hooks/useDmsPalette';

export default function DmsSearchBar({ onResults, initialQuery }) {
  const MP = useDmsPalette();
  const [query, setQuery] = useState(initialQuery || '');
  const [aiMode, setAiMode] = useState(false);
  const [vault, setVault] = useState('');
  const [status, setStatus] = useState('');
  const [searching, setSearching] = useState(false);
  const timerRef = useRef(null);
  const initialQueryRef = useRef(null);

  const VAULTS = ['procedures', 'legal', 'technical', 'financial', 'marketing', 'contracts', 'customer', 'hr', 'compliance'];
  const STATUSES = ['draft', 'review', 'published', 'archived'];

  const executeSearch = useCallback(async (q) => {
    if (!q.trim()) { onResults(null); return; }
    setSearching(true);
    const start = Date.now();
    try {
      let data;
      if (aiMode) {
        data = await dmsApi.aiSearch(q);
      } else {
        const params = { q };
        if (vault) params.vault = vault;
        if (status) params.status = status;
        data = await dmsApi.search(params);
      }
      const elapsed = Date.now() - start;
      onResults({
        results: data.documents || data.results || data || [],
        time: elapsed,
        query: q,
        isAiSearch: aiMode,
        aiInterpretation: data.interpretation || data.aiInterpretation || null,
      });
    } catch (err) {
      onResults({ results: [], error: err.error || err.message || 'Search failed', query: q });
    } finally {
      setSearching(false);
    }
  }, [aiMode, vault, status, onResults]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => executeSearch(val), 300);
  };

  // Re-search when filters change
  useEffect(() => {
    if (query.trim()) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => executeSearch(query), 300);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [vault, status, aiMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-trigger search when a new initialQuery is provided (e.g. entity click)
  useEffect(() => {
    if (initialQuery && initialQuery !== initialQueryRef.current) {
      initialQueryRef.current = initialQuery;
      setQuery(initialQuery);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => executeSearch(initialQuery), 100);
    }
  }, [initialQuery, executeSearch]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <TextField
          fullWidth
          placeholder={aiMode ? 'Ask anything about your documents…' : 'Search documents…'}
          value={query}
          onChange={handleChange}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searching ? <CircularProgress size={18} /> : aiMode ? <AutoAwesomeIcon sx={{ color: MP.green }} /> : <SearchIcon />}
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: MP.surface } }}
        />
        <FormControlLabel
          control={<Switch checked={aiMode} onChange={(e) => setAiMode(e.target.checked)} color="secondary" size="small" />}
          label={aiMode ? 'AI Search' : 'Standard'}
          sx={{ whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: aiMode ? MP.green : MP.textSecondary } }}
        />
      </Box>

      {!aiMode && (
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Vault</InputLabel>
            <Select value={vault} onChange={(e) => setVault(e.target.value)} label="Vault">
              <MenuItem value="">All Vaults</MenuItem>
              {VAULTS.map(v => <MenuItem key={v} value={v} sx={{ textTransform: 'capitalize' }}>{v}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
              <MenuItem value="">Any Status</MenuItem>
              {STATUSES.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      )}
    </Box>
  );
}
